import React, { useEffect, useMemo } from "react";
import { Paper, Typography, Grid, Button, Box, Divider } from "@mui/material";
import ModeOutlinedIcon from "@mui/icons-material/ModeOutlined";
import "./provider-profile-view.scss";

import { ReactComponent as PhotoIcon } from "../../../../assets/icons/photo-icon.svg";

type Address = Record<string, string | number | null | undefined> & {
  id?: string | number;
};

export type ProviderProfileDetails = {
  fullName?: string;
  businessEmail?: string;
  phoneNumber?: string;
  providerId?: string | number;
  provider_id?: string | number;
  address?: Address | null;

  //  add these (API may return either)
  profileImage?: string | null;
  profile_photo?: string | null;
  profilePhoto?: string | null;
};

type ProviderProfileViewProps = {
  onEdit: () => void;
  providerProfileDetails?: ProviderProfileDetails | null;
  isEditMode?: boolean;
};

const ProviderProfileView: React.FC<ProviderProfileViewProps> = ({
  onEdit,
  providerProfileDetails,
  isEditMode,
}) => {
  const profile = providerProfileDetails ?? {};

  // Remove id from address
  const address = useMemo<Record<string, string | number | null | undefined>>(() => {
    const addr = profile.address;
    if (!addr) return {};
    return Object.fromEntries(Object.entries(addr).filter(([key]) => key !== "id")) as Record<
      string,
      string | number | null | undefined
    >;
  }, [profile.address]);

  const fullName = profile.fullName ?? "";
  const businessEmail = profile.businessEmail ?? "";
  const phone = profile.phoneNumber ?? "";
  const providerId = (profile.providerId ?? profile.provider_id ?? "") as string | number | "";

  //  profile photo
  const profilePhoto =
    profile.profileImage ?? profile.profile_photo ?? profile.profilePhoto ?? null;

  useEffect(() => { }, [isEditMode]);

  return (
    <Paper className="provider-profile-view">
      {/* Header */}
      <Box className="provider-profile-view__header">
        <Box className="provider-profile-view__header-left">
          {/*  Avatar like form */}
          <Box
            className="provider-profile-view__avatarWrap"
            onClick={onEdit}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onEdit();
            }}
          >
            <Box className="provider-profile-view__avatar">
              {profilePhoto ? (
                <img
                  src={profilePhoto}
                  alt="Profile"
                  className="provider-profile-view__avatarImg"
                />
              ) : (
                <PhotoIcon className="provider-profile-view__photoIcon" />
              )}
            </Box>
          </Box>

          {/*  Name + subtitle */}
          <Box className="provider-profile-view__nameBlock">
            <Typography className="provider-profile-view__user-name">
              {fullName || "-"}
            </Typography>
            {/* <Typography className="provider-profile-view__user-profile-text">
              Provider Profile
            </Typography> */}
          </Box>
        </Box>

        <Button
          onClick={onEdit}
          className="provider-profile-view__edit-btn"
          startIcon={<ModeOutlinedIcon />}
        >
          Edit Profile
        </Button>
      </Box>

      <Divider />

      {/* Basic Information */}
      <Box className="provider-profile-view__section">
        <Typography variant="h6" className="provider-profile-view__section-title">
          Basic Information
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <p className="provider-profile-view__label">Full Name</p>
            <p className="provider-profile-view__value">{fullName || "-"}</p>
          </Grid>

          <Grid item xs={12} sm={6}>
            <p className="provider-profile-view__label">Provider ID</p>
            <p className="provider-profile-view__value">
              {providerId === "" ? "-" : providerId}
            </p>
          </Grid>

          <Grid item xs={12} sm={6}>
            <p className="provider-profile-view__label">Business Email Address</p>
            <p className="provider-profile-view__value">{businessEmail || "-"}</p>
          </Grid>

          <Grid item xs={12} sm={6}>
            <p className="provider-profile-view__label">Phone Number</p>
            <p className="provider-profile-view__value">{phone || "-"}</p>
          </Grid>
        </Grid>
      </Box>

      <Divider />

      {/* Address Information */}
      <Box className="provider-profile-view__section">
        <Typography variant="h6" className="provider-profile-view__section-title">
          Address Information
        </Typography>

        {Object.keys(address).length > 0 ? (
          <Grid container spacing={1}>
            {Object.entries(address).map(([key, value]) => (
              <Grid key={key} item xs={12} sm={6}>
                <p className="provider-profile-view__label">
                  {key === "street" ? "Street/Area" : key.charAt(0).toUpperCase() + key.slice(1)}
                </p>
                <p className="provider-profile-view__value">
                  {value === null || value === undefined || value === "" ? "-" : String(value)}
                </p>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography className="provider-profile-view__no-data">
            No address available
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default ProviderProfileView;
